import styled, { keyframes } from "styled-components";
import { connect } from "react-redux";
import { ArrowRight } from "@phosphor-icons/react";

import banner from "../images/banner/1.jpeg";
import { showLoginModal, signOutAPI } from "../store/actions";
import { Button, ButtonLink } from "./Shared/ui";

const Banner = ({ user, showLoginModal, signOut }) => {
  return (
    <Hero id="home">
      <Media>
        <img src={banner} alt="A happy couple sitting together on a lawn" fetchPriority="high" />
      </Media>
      <NavFade aria-hidden="true" />
      <Content>
        <TextBlock>
        <Title style={{ "--d": "120ms" }}>
          A trusted way to find your <em>life partner</em>
        </Title>
        <Sub style={{ "--d": "240ms" }}>
          Verified profiles from Kerala families, run by Vinayaka Foundation for more than 12 years.
        </Sub>
        <Ctas style={{ "--d": "360ms" }}>
          {user ? (
            <>
              <ButtonLink href="/account">
                My account <ArrowRight size={18} weight="bold" />
              </ButtonLink>
              <Button $variant="light" onClick={signOut}>Log out</Button>
            </>
          ) : (
            <>
              <ButtonLink href="#register">
                Register free <ArrowRight size={18} weight="bold" />
              </ButtonLink>
              <Button $variant="light" onClick={showLoginModal}>Log in</Button>
            </>
          )}
        </Ctas>
        </TextBlock>
      </Content>
    </Hero>
  );
};

const settle = keyframes`
  from { transform: scale(1.08); }
  to { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const rise = keyframes`
  from { opacity: 0; transform: translateY(28px); }
  to { opacity: 1; transform: none; }
`;

const Hero = styled.section`
  position: relative;
  min-height: min(100dvh, 880px);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  isolation: isolate;
  background: var(--navy);
  color: #fefefe;

  /* On phones the wide photo can't be cropped to portrait without losing one
     of the couple, so the photo sits on top at 4:3 and the copy below it. */
  @media (max-width: 768px) {
    display: block;
    min-height: 0;
    padding-top: var(--header-h);
    background: rgb(24 64 46);
  }
`;

const Media = styled.div`
  position: absolute;
  inset: 0;
  z-index: -2;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: 62% 30%;
    animation: ${settle} 2.4s var(--ease-out) both;
  }

  @media (max-width: 768px) {
    position: relative;
    inset: auto;
    aspect-ratio: 4 / 3;
    overflow: hidden;

    img {
      object-position: 55% 30%;
    }

    &::after {
      content: "";
      position: absolute;
      inset: auto 0 0 0;
      height: 35%;
      background: linear-gradient(0deg, rgb(24 64 46) 0%, rgb(24 64 46 / 0) 100%);
    }
  }
`;

// Short fade under the transparent header so the nav links stay readable.
const NavFade = styled.div`
  position: absolute;
  inset: 0 0 auto 0;
  height: 140px;
  z-index: -1;
  background: linear-gradient(180deg, rgb(36 48 94 / 0.45) 0%, rgb(36 48 94 / 0) 100%);

  @media (max-width: 768px) {
    display: none;
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: var(--max-w);
  margin: 0 auto;
  padding: calc(var(--header-h) + 48px) var(--gutter) clamp(56px, 9vh, 96px);

  @media (max-width: 768px) {
    padding: 4px var(--gutter) 44px;
  }
`;

// The tinted backdrop sits only behind the copy: a feathered radial glow
// sized to the text block, so the faces in the photo keep their colour.
const TextBlock = styled.div`
  position: relative;
  isolation: isolate;
  width: fit-content;
  max-width: 100%;

  &::before {
    content: "";
    position: absolute;
    z-index: -1;
    inset: -100px -160px -200px -140px;
    background: radial-gradient(
      closest-side,
      rgb(24 64 46 / 0.9) 0%,
      rgb(24 64 46 / 0.82) 55%,
      rgb(24 64 46 / 0.45) 78%,
      rgb(24 64 46 / 0) 100%
    );
    filter: blur(8px);
    pointer-events: none;
    animation: ${fadeIn} 1.2s var(--ease-out) both;
  }

  > h1,
  > p {
    text-shadow: 0 2px 18px rgb(18 23 46 / 0.35);
  }

  > * {
    animation: ${rise} 1s var(--ease-out) both;
    animation-delay: var(--d, 0ms);
  }

  @media (max-width: 768px) {
    &::before {
      display: none;
    }
  }
`;

const Title = styled.h1`
  color: #fefefe;
  font-size: clamp(40px, 5.4vw, 68px);
  line-height: 1.04;
  letter-spacing: -0.035em;
  font-weight: 600;
  max-width: 17ch;
  margin-bottom: 20px;

  em {
    font-style: normal;
    color: var(--butter);
  }
`;

const Sub = styled.p`
  font-size: clamp(17px, 1.5vw, 20px);
  line-height: 1.6;
  max-width: 44ch;
  color: rgb(254 254 254 / 0.88);
  margin-bottom: 32px;
`;

const Ctas = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 480px) {
    > * {
      flex: 1 1 100%;
    }
  }
`;

const mapStateToProps = (state) => ({
  user: state.userState.user,
});

const mapDispatchToProps = (dispatch) => ({
  showLoginModal: () => dispatch(showLoginModal()),
  signOut: () => dispatch(signOutAPI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Banner);
